import { observable, action, computed, reaction } from 'mobx';
import objectPath from 'object-path';
import i18next from 'i18next';
import Alert from 'react-s-alert';
import { notificationCenter } from 'apps/notificationCenter/appManager';
import { frameStore } from 'apps/components/Frame/appManager';
import { analytics } from 'core/analytics';
import { fromLocalStorage, toLocalStorage } from 'core/localStorage';
import {
  disconnectWebsocket,
  initHMACWebsocket,
  initWebsocket,
} from 'core/client';
import { routerStore } from 'core/routing';
import {
  startJwtRefreshing,
  stopJwtRefreshing,
  RemoveJwt,
  tokenData,
  refresher,
  getJwt,
  getRefreshJwt,
} from 'core/jwt';
import { AppRegistry, ModelRegistry } from 'core/registry';
import { AppManager, client } from 'core';

import { SpaceModel } from 'apps/space/app';
import { isMobile } from 'styles/utils';

import { i18n } from 'config/i18n';

import { maxWidths } from 'legacyComponents/styled/styleUtils';

import { WHOAMI } from 'apps/signIn/gql';
import { Uploader } from 'apps/client/uploader';

import Cookie from 'js-cookie';
import {
  TEAMS,
  PROFILE_SPACE,
  INITIATIVE_SPACE,
  PROFILE_PLAN_VALIDATION,
  INITIATIVE_PLAN_VALIDATION,
} from 'apps/space/gql';
import { routesWithoutQueryProfileSpace } from 'constants/global';

import {
  communityRoutes,
  conditionalCommunityRoutes,
  conditionalProjectRoutes,
} from 'apps/components/Frame/config';
import { isInitiativeSlug, isProfileSlug } from 'utils';
import {
  listenNavigation,
  isInDesktopMode,
  getCurrentTabIdFromDesktop,
  getHomeTabIdFromDesktop,
} from 'libs/gutClient/electronBridge';

import { createNotification } from 'apps/utils';
import { FrameHeaderHeight, SubHeaderHeight } from 'styles/constants';

import { isDesktopCompatibility } from 'hooks/useDesktopCompatible';

import { ContributionsSubscription } from './gql';

export class ClientStore extends AppManager {
  routerStore = routerStore;
  @observable profileResponse = null;
  @observable posting = false;
  @observable context = observable.map({}, { deep: false });
  @observable footerHidden = false;
  @observable flash = null;
  @observable pendingInvitations = [];
  @observable hasBanner = false;
  @observable hasCovidNav = true;
  @observable isStaff = false;
  @observable currentSpaceData = null;
  @observable isBusinessPlanOfCurrentSpace = false;
  @observable flags = {};
  @observable isMobile = isMobile();
  @observable teamsQueryStatus = 'init';
  @observable profileQueryStatus = 'init';
  @observable currentTabId = -1; // for desktop mode
  @observable homeTabId = -1; // for desktop mode

  @observable workspaces = [];
  @observable activeSpaceId = null;
  // The space ID determines the page display which initiative card
  @observable activeInitiativeSpaceId = null;
  @observable areWorkspacesLoaded = false;

  @observable activeSpaceChangedCount = 0;

  @observable HMAC = null;
  @observable profileSpace = null;

  routesReactiveToActiveSpace = [
    'workspace',
    'workspace.spaceFiles',
    'workspace.spaceProjects',
    'workspace.spaceSettings.teams',
    'workspace.spaceSettings.account',
    'workspace.spaceSettings.notifications',
    'workspace.spaceSettings.billing',
    'workspace.rfqs',
    'workspace.orders',
    'workspace.intermediator.rfqs',
    'workspace.intermediator.orders',
    'workspace.manufacturer.rfqs',
    'workspace.manufacturer.orders',
  ];

  routesWithInherentSpace = [
    'profile',
    'initiative.project',
    'initiative.project.files',
    'initiative.project.issues',
    'initiative.project.settings.teams',
    'initiative.project.settings.general',
    'profile.project',
    'profile.project.files',
    'profile.project.issues',
    'profile.project.settings.teams',
    'profile.project.settings.general',
    'initiative',
    'initiative.projects',
    'initiative.stories',
    'initiative.shared.file',
    'initiative.project.file',
    'profile.shared.file',
    'profile.project.file',
  ];

  publicBaseRoutes = ['profile', 'initiative', 'home', 'channel', 'make-part'];

  @action initialize = () => {
    this.tokenData = tokenData;
    const ProfileApp = AppRegistry.get('ProfileApp');
    const ProfileModel = ModelRegistry.get('ProfileModel');

    // NOTE (david): AppRef should be set in the constructor, otherwise reactions may to not occur if an
    // observer accesses "signedInProfile" before it's been setup with AppRef. In that case even when the
    // content is initialized the observer will not react because there was no observable in the first place.
    // Just declaring @observable signedInProfile = null; in this module does not fix this.
    // This is not in the constructor due to an issue with circular import due this being a global singleton
    // For now we hopefully call initialize() early enough that this is initialized before any
    // access to that variable from an observer happens.
    this.AppRef('signedInProfile', ProfileApp);
    this.SetSignedInProfile(
      ProfileModel.Load(fromLocalStorage('signedInProfile')),
      { toLS: false },
    );
    this.WhoAmI();

    this.LoadHMAC();
    this.uploader = new Uploader();
    this.readFlashMessage();
    this.initReactionsForSpaceAwareRoutes();

    this.initDesktop();

    this.disposers.push(
      reaction(
        () => ({ slug: this.slugFromRouteOrSpace }),
        ({ slug }) => {
          if (slug) {
            this.QuerySpaceForPlan(slug);
            this.MaybeAssignActiveWorkspaceFromRoute();
          }
        },
      ),
    );
  };

  async initDesktop() {
    if (isInDesktopMode()) {
      this.subscribeContributions();

      const pathWithAuth = ['home'];

      try {
        fetch('index', { mode: 'cors' }).then(async response => {
          const { headers } = response;

          const compatibleWithTab = await isDesktopCompatibility({
            feature: 'tab',
          });

          if (compatibleWithTab) {
            getCurrentTabIdFromDesktop().then(tabId => {
              this.currentTabId = tabId;
              const crc = headers.get('x-oss-hash-crc64ecma');

              if (crc) {
                window.localStorage.setItem(`${tabId}-indexChecksum`, crc);
              }
            });

            getHomeTabIdFromDesktop().then(homeTabId => {
              this.homeTabId = homeTabId;
            });
          } else {
            const crc = headers.get('x-oss-hash-crc64ecma');

            if (crc) {
              window.localStorage.setItem(`indexChecksum`, crc);
            }
          }
        });
      } catch (error) {
        analytics.logError(error);
      }

      listenNavigation((_, path, param, opts) => {
        if (param.type) {
          if (param.type === 'generate_space_path') {
            param.spacePath =
              routerStore.route.params.spacePath ||
              routerStore.route.params.profile ||
              routerStore.route.params.initiative;
          }

          delete param.type;
        }

        if (pathWithAuth.includes(path) && !this.signedInProfile) {
          return;
        }

        routerStore.navigate(path, param, opts);
      });
    }
  }

  @action subscribeContributions = () => {
    this.contributionSubscription = new ContributionsSubscription(this, {
      variables: {},
    });

    this.contributionSubscription.StartSubscription();

    this.contributionSubscription.process(result => {
      if (result) {
        createNotification({
          title: "You've got a new contribution!",
          notificationId: result.id,
          noticeOnWeb: false,
          options: {
            body: `${result.creator.profile.username}: ${result.title}`,
            tag: result.id,
          },
          navigationTo: {
            project: {
              spaceType: result.project.space.spaceType,
              prefix: result.project.space.spaceType === 'profile' ? '@' : '+',
              spaceSlug: result.project.space.content.slug,
              projectSlug: result.project.slug,
            },
          },
          tabId: this.currentTabId,
        });
      }
    });
  };

  @action initReactionsForSpaceAwareRoutes() {
    // Reaction 1: If the route changes, check whether we have to move to the active space
    // set by the route.
    this.disposers.push(
      reaction(
        () => ({
          params: routerStore.route.params,
        }),
        () => {
          this.MaybeAssignActiveWorkspaceFromRoute();
        },
      ),
    );

    // Reaction 2: If the active space changes, check whether we have to change the route displayed.
    this.disposers.push(
      reaction(
        () => this.activeSpace,
        activeSpace => {
          if (!activeSpace) return;
          const { name, params } = routerStore.route;
          if (this.routesReactiveToActiveSpace.includes(name)) {
            const paramSpacePath = params?.spacePath;
            if (paramSpacePath !== activeSpace.path) {
              routerStore.router.navigate(name, {
                ...params,
                spacePath: activeSpace.path,
              });
            }
          }
        },
      ),
    );

    // Reaction 3: Redirect home to workspace (dahsboard) route
    this.disposers.push(
      reaction(
        () => ({
          signedInProfile: this.signedInProfile,
          routeName: routerStore.route.name,
        }),
        ({ signedInProfile, routeName }) => {
          if (!signedInProfile) return;
          if (routeName === 'home') {
            let spacePath = this.activeSpace?.path;
            if (!spacePath) spacePath = signedInProfile.username;
            routerStore.navigate(
              'workspace',
              {
                spacePath,
              },
              { replace: true },
            );
          }
        },
        { fireImmediately: true },
      ),
    );
  }

  @action MaybeAssignActiveWorkspaceFromRoute() {
    const { name, params } = routerStore.route;
    if (this.routesReactiveToActiveSpace.includes(name)) {
      const paramSpacePath = params?.spacePath;
      if (paramSpacePath) {
        const routeSpace = this.workspaces.find(
          workspace => workspace?.path === paramSpacePath,
        );
        if (routeSpace) {
          this.SetActiveWorkspace(routeSpace.id);
        }
      }
    } else if (this.routesWithInherentSpace.includes(name)) {
      const paramSpacePath = params?.profile || params?.initiative;
      if (paramSpacePath) {
        const routeSpace = this.workspaces.find(
          workspace => workspace?.path === paramSpacePath,
        );
        if (routeSpace) {
          this.SetActiveWorkspace(routeSpace.id);
        }
      }
    }
  }

  @action MaybeNavigateToTheDefaultSpaceRoute() {
    const routeName = routerStore.route.name;
    if (!this.routesReactiveToActiveSpace.includes(routeName)) {
      routerStore.navigate('home');
    }
  }

  next() {
    const nextParam = this.routerStore.params.get('next');
    if (nextParam) {
      return nextParam;
    }
    return window.location.pathname + window.location.search;
  }

  @action readFlashMessage = () => {
    const flashCookie = Cookie.getJSON('flash_messages');
    if (flashCookie) {
      const decodedCookie = atob(flashCookie);
      try {
        const cookieObject = JSON.parse(decodedCookie);
        this.flash = cookieObject;
      } finally {
        Cookie.set('flash_messages', '', { expires: 0 });
      }
    }
  };

  @action SetupGlobalSubscriptions = () => {
    const route = routerStore.route.name.split('.');
    if (route && route.length > 0 && route[0] === 'viewer') {
      return;
    }

    if (this.signedInProfile && !notificationCenter.isSubscribed) {
      notificationCenter.SubscribeNotification();
    } else if (!this.signedInProfile && notificationCenter.isSubscribed) {
      notificationCenter.StopSubscribeNotification();
    }

    if (this.signedInProfile && !frameStore.inboxManager?.isSubscribed) {
      frameStore.inboxManager?.SubscribeInbox();
    } else if (!this.signedInProfile && frameStore.inboxManager?.isSubscribed) {
      frameStore.inboxManager?.StopSubscribeInbox();
    }
  };

  @action ResetFlash = () => {
    this.flash = null;
  };

  @action SetPendingInvitations = pendingInvitations => {
    this.pendingInvitations = pendingInvitations;
  };

  @action RemoveInvitation = invitation => {
    this.pendingInvitations = this.pendingInvitations.filter(
      ({ id }) => id !== invitation.id,
    );
  };

  @action SetSignedInProfile = (profile, options = { toLS: true }) => {
    const { toLS } = options;
    this.signedInProfile = profile;
    if (profile) {
      startJwtRefreshing();
      analytics.linkUser(profile);
      i18n.changeLanguage(profile.locale);
      initWebsocket();

      if (isInDesktopMode()) {
        const accessToken = getJwt();
        const refreshToken = getRefreshJwt();

        if (refreshToken) {
          window.ipc.invoke('login', accessToken, refreshToken);
        }
      }
    } else {
      analytics.unlinkUser();
      this.SetIsStaff(false);
    }
    if (toLS) {
      toLocalStorage('signedInProfile', profile);
    }
    this.SetupGlobalSubscriptions();
  };

  @action onSignOutErr = () => {
    Alert.error(
      i18next.t(
        'Sorry, we were unable to sign you out. Please check your network connection and try again.',
      ),
    );
  };

  @action SignOutUser = (error, redirectPath) => {
    if (error) {
      this.context.get('signInAppManager').SignOut({
        onSysErr: this.SignOutWithAlert.bind(this, error, redirectPath),
        onUserErr: this.SignOutWithAlert.bind(this, error, redirectPath),
        onSuccess: this.SignOutWithAlert.bind(this, error, redirectPath),
      });
    } else {
      this.context.get('signInAppManager').SignOut({
        onSysErr: this.CompleteSignOut,
        onUserErr: this.CompleteSignOut,
        onSuccess: this.CompleteSignOut,
      });
    }
  };

  @action SignOutWithAlert = (alert, redirectPath) => {
    routerStore.navigate('home.signIn', {
      next: redirectPath || this.next(),
    });

    this.ClearUserDataAndDisconnect();

    if (alert.level && alert.message) {
      Alert[alert.level](i18next.t(alert.message));
    }
  };

  @action ClearUserDataAndDisconnect = () => {
    client.stop();

    this.SetSignedInProfile(null);

    disconnectWebsocket();
    // remove the jwt and stop the refreshing
    stopJwtRefreshing();
    RemoveJwt();

    this.areWorkspacesLoaded = false;
    this.workspaces = [];
    client.clearStore();
  };

  @action CompleteSignOut = () => {
    routerStore.navigate('home');

    this.ClearUserDataAndDisconnect();

    if (isInDesktopMode()) {
      window.ipc.invoke('logout');
    }

    this.clearImpersonationSession();

    if (
      this.routerStore.route.name !== 'home.signIn' &&
      this.routerStore.route.name !== 'home.signUp'
    ) {
      window.location.reload();
    }
  };

  @action UpdateProfileOrTeamWorkspace = slug => {
    const querySlug = slug || this.slugFromRouteOrSpace;
    if (!this.signedInProfile) return;

    if (isInitiativeSlug(querySlug)) {
      this.QueryCurrentTeamWorkspace(querySlug);
      return;
    }

    this.QueryProfileWorkspace();
  };

  @action UpdateWorkSpaces = workspace => {
    const updatedIndex = this.workspaces.findIndex(
      ({ id }) => id === workspace.inSpace.id,
    );

    const modelData = SpaceModel.Load(workspace);
    const workspaceExisted = updatedIndex !== -1;

    if (workspaceExisted) {
      this.workspaces[updatedIndex] = modelData;
    } else {
      this.workspaces.push(modelData);
    }

    return modelData;
  };

  @action UpdateProfileSpace = profile => {
    if (!profile) {
      return null;
    }
    const content = {
      ...(this.profileSpace?.content || {}),
      ...(profile.content || {}),
    };

    const modelData = this.UpdateWorkSpaces(
      this.convertToSpaceData({
        ...profile,
        content,
      }),
    );

    this.profileSpace = {
      ...(this.profileSpace || {}),
      ...profile,
      content,
      isPro: profile.inSpace?.subscriptionType === 'PRO',
    };

    return modelData;
  };

  @action UpdateTeamSpace = initiative => {
    if (!initiative) {
      return;
    }

    this.UpdateWorkSpaces(this.convertToSpaceData(initiative));
  };

  @action QueryProfileWorkspace = async () => {
    if (
      routesWithoutQueryProfileSpace.includes(this.routerStore.route.name) &&
      !this.workspaces?.length
    ) {
      return null;
    }
    try {
      this.profileQueryStatus = 'loading';
      const { username } = this.signedInProfile || {};
      const { data } = await client.query({
        query: PROFILE_SPACE,
        fetchPolicy: 'network-only',
        variables: {
          username,
        },
      });

      const { profile } = data;

      this.profileQueryStatus = 'done';

      return this.UpdateProfileSpace(profile);
    } catch (error) {
      analytics.logError(error);
      this.profileQueryStatus = 'done';
      console.error('query profile space error: ', error?.message || error);
      return null;
    }
  };

  @action QueryCurrentTeamWorkspace = async slug => {
    const { data } = await client.query({
      query: INITIATIVE_SPACE,
      fetchPolicy: 'network-only',
      variables: {
        slug,
      },
    });

    if (!data) return;

    const { initiative } = data;

    const updatedIndex = this.workspaces.findIndex(
      ({ id }) => id === initiative.inSpace.id,
    );

    if (updatedIndex === -1) return;

    this.workspaces[updatedIndex] = SpaceModel.Load(
      this.convertToSpaceData(initiative),
    );
  };

  @action QueryTeamWorkspaces = async () => {
    if (routesWithoutQueryProfileSpace.includes(this.routerStore.route.name)) {
      return [];
    }
    try {
      this.teamsQueryStatus = 'loading';

      const { data } = await client.query({
        query: TEAMS,
        fetchPolicy: 'network-only',
      });

      const { teams } = data;
      this.teamsQueryStatus = 'done';

      return teams
        .map(team => SpaceModel.Load(this.convertToSpaceData(team)))
        .sort((a, b) => {
          if (a.spaceType === b.spaceType) return 0;
          return 1;
        });
    } catch (error) {
      analytics.logError(error);
      this.teamsQueryStatus = 'done';
      console.error('query profile space error: ', error?.message || error);
      return [];
    }
  };

  @action QuerySpaceForPlan = async slug => {
    if (!slug) return null;
    let result = null;

    // clear currentSpaceData for every time
    this.currentSpaceData = null;
    const validationObj =
      '{"class_name":"Space","space_type":{"name":"PROJECT"}}';

    try {
      if (isProfileSlug(slug)) {
        result = await client.query({
          query: PROFILE_PLAN_VALIDATION,
          fetchPolicy: 'network-only',
          variables: {
            username: slug,
            obj: validationObj,
          },
        });
        this.isBusinessPlanOfCurrentSpace = false;
        this.currentSpaceData = result.data.profile;
      } else {
        result = await client.query({
          query: INITIATIVE_PLAN_VALIDATION,
          fetchPolicy: 'network-only',
          variables: {
            slug,
            obj: validationObj,
          },
        });
        this.currentSpaceData = result.data.initiative;

        result.data.initiative.billingSubscriptions.forEach(subscription => {
          if (subscription.plan?.name?.startsWith('Business')) {
            this.isBusinessPlanOfCurrentSpace = true;
            return;
          }

          this.isBusinessPlanOfCurrentSpace = false;
        });
      }

      return result;
    } catch (e) {
      analytics.logError(e);
      console.error(e?.message);
      return null;
    }
  };

  @action UpdateProfileResponse = profile => {
    this.profileResponse = profile;
  };

  async WhoAmI() {
    const params = { query: WHOAMI, fetchPolicy: 'no-cache' };
    try {
      const { data } = await client.query(params);
      const ProfileModel = ModelRegistry.get('ProfileModel');

      const profileData = objectPath.get(data, 'loggedInUser.profile', null);
      const isStaff = objectPath.get(data, 'loggedInUser.isStaff', false);
      const flagsJSON = objectPath.get(data, 'loggedInUser.flagsJSON', false);

      this.UpdateProfileResponse(profileData);

      if (this.profileResponse) {
        this.profileResponse.isStaff = data?.loggedInUser?.isStaff;
      }

      this.UpdateProfileSpace(profileData);
      const profile = ProfileModel.Load(profileData);

      this.SetSignedInProfile(profile);
      this.SetIsStaff(isStaff);
      const flags = JSON.parse(flagsJSON);
      this.SetFlags(flags);
      if (profile)
        this.QueryAvailableSpaces(this.activeSpaceChangedCount === 0);
      else {
        this.workspaces = [];
        this.SetActiveWorkspace(null);
      }
      return profile;
    } catch (err) {
      analytics.logError(err);
    }

    return null;
  }

  // Role and header management

  hasRole = roleName => {
    if (roleName === 'USER') {
      return true; // check for logged in?
    }
    if (roleName === 'INTERMEDIATOR') {
      if (tokenData.jwtData?.role === 'INTERMEDIATOR') {
        return true;
      }
      return false;
    }
    if (roleName === 'ADMIN') {
      if (tokenData.jwtData?.role === 'ADMIN') {
        return true;
      }
      return false;
    }
    return false;
  };

  @computed get currentPortal() {
    if (!tokenData.jwtData?.role) {
      return '';
    }
    if (
      (this.hasRole('INTERMEDIATOR') &&
        routerStore.route.name.startsWith('intermediator')) ||
      routerStore.route.name.startsWith('workspace.intermediator')
    ) {
      return 'INTERMEDIATOR';
    }
    return 'USER';
  }

  @action ToggleCurrentPortal = () => {
    const routeName = routerStore.route.name;
    const newParams = Object.assign({}, routerStore.route.params);
    // remove filters when switching portals as we may have different filters available
    delete newParams.filters;

    if (this.currentPortal === 'USER' && this.hasRole('INTERMEDIATOR')) {
      if (routeName.startsWith('workspace')) {
        routerStore.router.navigate(
          routeName.replace('workspace', 'workspace.intermediator'),
          newParams,
        );
      } else {
        routerStore.router.navigate(`intermediator.${routeName}`, newParams);
      }
    } else if (this.currentPortal === 'INTERMEDIATOR') {
      routerStore.router.navigate(
        routeName
          .replace('workspace.intermediator', 'workspace')
          .replace('intermediator.', ''),
        newParams,
      );
    }
  };

  @computed get roleHeaders() {
    if (this.currentPortal === 'USER') {
      return { 'X-Wikifactory-Role': 'USER' };
    }
    return {};
  }

  @computed get routeSegments() {
    return routerStore.route.name.split('.');
  }

  // Space utils
  @computed get spaceRoute() {
    const route = routerStore.route.name.split('.');
    let sRoute = route[0];
    if (!['profile', 'initiative', 'channel', 'project'].includes(sRoute)) {
      // return {route:'home', exact: (sRoute === 'home')};
      return { route: 'home', exact: route.length === 1 };
    }
    if (route[1] === 'project' || route[1] === 'collection') {
      sRoute = route.slice(0, 2).join('.');
    }
    return {
      route: sRoute,
      exact: sRoute === routerStore.route.name,
      type: sRoute.split('.')[-1],
    };
  }

  @computed get spaceParams() {
    const spaceRoute = this.spaceRoute.route;
    if (['profile', 'initiative'].includes(spaceRoute)) {
      return { [`${spaceRoute}`]: routerStore.params.get(spaceRoute) };
    }
    if (spaceRoute === 'channel') {
      return { channel: routerStore.params.get('channel') };
    }
    if (spaceRoute === 'profile.project') {
      return {
        profile: routerStore.params.get('profile'),
        slug: routerStore.params.get('slug'),
      };
    }
    if (spaceRoute === 'initiative.project') {
      return {
        initiative: routerStore.params.get('initiative'),
        slug: routerStore.params.get('slug'),
      };
    }
    return {};
  }

  @computed get spacePath() {
    return routerStore.router
      .buildPath(this.spaceRoute.route, this.spaceParams)
      .slice(1);
  }

  @computed get atCommunityRoute() {
    if (communityRoutes.includes(routerStore.route.name)) return true;

    if (
      conditionalProjectRoutes.includes(routerStore.route.name) &&
      frameStore.project?.private
    ) {
      return false;
    }

    const routeSlugPath =
      routerStore.route.params.profile || routerStore.route.params.initiative;
    if (
      conditionalCommunityRoutes.includes(routerStore.route.name) &&
      !this.getWorkspaceWithSlugPath(routeSlugPath)
    ) {
      return true;
    }

    return false;
  }

  @observable locale = 'en';
  @action SetPosting = bool => {
    this.posting = bool;
  };

  @action SetFooterHidden = bool => {
    this.footerHidden = bool;
  };

  @observable scrollTop = 0;
  @observable checkingScroll = false;
  @action ScrollToTop = () => {
    window.requestAnimationFrame(() => {
      window.scrollTo(0, 0);
    });
  };

  @computed get headerIsFixed() {
    if (this.routerStore.route.name.includes('home.discover')) {
      return false;
    }
    const fixedHeaderRoutes = [
      'profile',
      'profile.project',
      'profile.collection',
      'initiative',
      'initiative.project',
      'initiative.collection',
    ];
    if (
      fixedHeaderRoutes.includes(this.spaceRoute.route) &&
      this.spaceRoute.exact
    ) {
      return false;
    }
    return true;
  }

  @observable windowWidth = window.innerWidth;
  @observable windowHeight = window.innerHeight;

  @computed get breakPointManager() {
    const isWiderThanMD = this.windowWidth > 768;
    const isSmallerThanM = this.windowWidth <= maxWidths.m;

    return {
      isWiderThanMD,
      isSmallerThanM,
    };
  }

  @computed get activeBreakPoint() {
    if (this.windowWidth > maxWidths.l) {
      return 'default';
    }
    if (this.windowWidth <= maxWidths.l && this.windowWidth > maxWidths.m) {
      return 'l';
    }
    if (this.windowWidth <= maxWidths.m && this.windowWidth > maxWidths.s) {
      return 'm';
    }
    if (this.windowWidth <= maxWidths.s && this.windowWidth > maxWidths.xs) {
      return 's';
    }
    if (this.windowWidth <= maxWidths.xs) {
      return 'xs';
    }
    return 'default';
  }

  @action setLocale(newLocale) {
    this.locale = newLocale;
  }

  @action setCheckingScroll(bool) {
    this.checkingScroll = bool;
  }

  @action setScrollTop(top) {
    this.scrollTop = top;
  }

  @action setWindowSize(width, height) {
    this.windowWidth = width;
    this.windowHeight = height;
  }

  @action SetHasBanner = bool => {
    this.hasBanner = bool;
    if (!bool) this.hasCovidNav = true;
  };

  @action SetIsStaff(staff) {
    this.isStaff = !!staff;
  }

  @action SetFlags(flags) {
    this.flags = flags;
  }

  @action convertToSpaceData = data => {
    if (!data) {
      return {};
    }

    const { inSpace, ...restData } = data;
    const { spaceType, id, subscriptionType } = inSpace;

    return {
      inSpace,
      ...restData,
      id,
      spaceType,
      subscriptionType,
    };
  };

  @action async QueryAvailableSpaces(
    shouldTryToSetActiveWorkspaceFromRoute = false,
    onSuccess,
  ) {
    if (this.hasRole('ADMIN')) {
      return;
    }

    try {
      const [profileSpace, teamSpaces] = await Promise.all([
        this.QueryProfileWorkspace(),
        this.QueryTeamWorkspaces(),
      ]);

      this.workspaces = [profileSpace, ...teamSpaces].filter(Boolean);

      // default first team spaceId is activeInitiativeSpaceId
      this.activeInitiativeSpaceId = teamSpaces[0]?.id;

      if (/*! spaces || */ this.workspaces.length === 0) {
        // only set flag to loaded if we got empty data

        // if (spaces && spaces.length === 0)
        this.areWorkspacesLoaded = true;
        this.SetActiveWorkspace(null);
        return;
      }

      if (shouldTryToSetActiveWorkspaceFromRoute) {
        this.MaybeAssignActiveWorkspaceFromRoute();
      }
      const doWeHaveAValidActiveSpace = this.activeSpace;

      const firstWorkspaceId = this.workspaces[0]?.id;

      if (!doWeHaveAValidActiveSpace && firstWorkspaceId) {
        this.SetActiveWorkspace(firstWorkspaceId);
      }

      this.areWorkspacesLoaded = true;
      onSuccess?.();
    } catch (error) {
      analytics.logError(error);
      console.error('QueryAvailableSpaces error: ', error?.message);
    }
  }

  // format: slug === 'user001'
  getWorkspaceWithSlug = slug => {
    if (!slug) return null;
    let sanitizedSlug = slug;
    // Ideally this should not be necessary, but it is because some slugs from server return as [@+]slug
    if (slug[0] === '+' || slug[0] === '@') {
      sanitizedSlug = slug.substring(1);
    }
    const matchingSpace = this.workspaces.find(
      workspace => workspace.content.slug === sanitizedSlug,
      // || workspace.content.slug === slug,
    );
    return matchingSpace;
  };

  // format: slugPath === '@user001'
  getWorkspaceWithSlugPath = slugPath => {
    if (!slugPath) return null;
    let slug = null;
    if (slugPath[0] === '+' || slugPath[0] === '@') {
      slug = slugPath.substring(1);
    }
    return this.getWorkspaceWithSlug(slug);
  };

  @action SetActiveWorkspaceWithSlugIfValid(slug) {
    return this.SetActiveWorkspaceIfValid(this.getWorkspaceWithSlug(slug)?.id);
  }

  @action SetActiveWorkspaceIfValid(spaceId) {
    if (!spaceId) return false;
    if (this.workspaces.some(workspace => workspace.id === spaceId)) {
      this.SetActiveWorkspace(spaceId);
      return true;
    }
    return false;
  }

  @action SetActiveWorkspace(spaceId) {
    if (spaceId && this.activeSpaceId !== spaceId)
      this.activeSpaceChangedCount += 1;
    this.activeSpaceId = spaceId;

    const currentSpace = this.workspaces.find(space => space.id === spaceId);

    if (currentSpace && currentSpace.spaceType === 'INITIATIVE') {
      this.activeInitiativeSpaceId = spaceId;
    }
  }

  @computed get activeSpace() {
    const currentSpace = this.workspaces.find(
      space => space.id === this.activeSpaceId,
    );

    return currentSpace;
  }

  @computed get initiativeSpaces() {
    return this.workspaces.filter(
      ({ spaceType }) => spaceType === 'INITIATIVE',
    );
  }

  @computed get activeInitiativeSpace() {
    const currentInitiative = this.workspaces.find(({ id }) => {
      return id === this.activeInitiativeSpaceId;
    });

    return {
      ...currentInitiative,
      isTeamSubscription: currentInitiative?.subscriptionType === 'TEAM',
    };
  }

  @computed get slugFromRouteOrSpace() {
    const spacePrefix = ['@', '+'];
    const { spacePath, initiative, profile } = routerStore.route.params;

    const slug = [spacePath, initiative, profile, this.activeSpace?.path].find(
      v => spacePrefix.some(prefix => v?.startsWith(prefix)),
    );

    return slug;
  }

  @computed get isInProfileSpace() {
    return isProfileSlug(this.slugFromRouteOrSpace);
  }

  @computed get operateChecker() {
    const isLoginUserSlug =
      this.slugFromRouteOrSpace === this.signedInProfile?.username;

    const { canDeleteIn, canCreateIn, ...rest } = this.currentSpaceData || {};

    return {
      canCreateIn: isLoginUserSlug || canCreateIn,
      canDeleteIn: isLoginUserSlug || canDeleteIn,
      ...rest,
    };
  }

  @computed get isTheCurrentActiveSpaceTheFirstOneUsed() {
    return this.activeSpaceChangedCount === 1;
  }

  @action SetIsMobile(_isMobile) {
    this.isMobile = _isMobile;
  }

  @computed get PageHeaderHeight() {
    // Under mobile environment, will hide subheader
    if (this.isMobile || isInDesktopMode()) {
      return FrameHeaderHeight;
    }

    return FrameHeaderHeight + SubHeaderHeight;
  }

  @action RequestHMAC = () => {
    fetch(`${API_URL_BASE}/hmac`, {})
      .then(response => {
        if (!response.ok) {
          throw Error(response.statusText);
        }
        return response.text();
      })
      .then(this.SetHMAC);
  };

  @action SetHMAC = newHMAC => {
    this.HMAC = newHMAC;
    initHMACWebsocket(this.HMAC);
    sessionStorage.setItem('HMAC', this.HMAC);
  };

  setCountdownSeconds = seconds => {
    sessionStorage.setItem('countdownSeconds', seconds);
  };

  getCountdownSeconds = () => {
    return sessionStorage.getItem('countdownSeconds');
  };

  clearCountdownSeconds = () => {
    sessionStorage.removeItem('countdownSeconds');
  };

  setIsImpersonating = bool => {
    sessionStorage.setItem('isImpersonating', bool);
  };

  clearImpersonationSession = () => {
    sessionStorage.removeItem('isImpersonating');
  };

  isImpersonating = () => {
    return sessionStorage.getItem('isImpersonating') === 'true';
  };

  @action LoadHMAC = () => {
    const storageHMAC = sessionStorage.getItem('HMAC');
    if (!storageHMAC) return;
    this.SetHMAC(storageHMAC);
  };
}

export const clientStore = new ClientStore(null, {}, { autoDispose: false });
refresher.clientStore = clientStore;
